import React, { useEffect, useState } from "react";
import DatePicker from "@/components/form/DatePicker";
import Dropdown, { Item } from "@/components/form/Dropdown";
import TextInput from "@/components/form/TextInput";
import {
  CreateStaffInfo,
  StaffInfo,
  StaffInfoDTO,
} from "@/models/staff-info.model";
import { nationalities } from "@/api/countries";
import { Button } from "@/components/ui/button";
import {
  genderOptions,
  nationalityOptions,
  raceOptions,
} from "../../onboard-page/StaffInfoForm";
import EditButton from "@/components/buttons/EditButton";
import {
  getStaffInfoByUserId,
  updateStaffInfoByStaffInfoId,
} from "@/api/staff-info";
import { toast } from "@/hooks/use-toast";
import { RoleType, User } from "@/models/user.model";
import UserRoleDropdown from "@/components/form/UserRoleDropdown";
import { CustomDatePicker } from "@/components/form/CustomDatePicker";
import { Loader2 } from "lucide-react";

interface Props {
  userId: number;
  user: User | null;
}

const roleOptions: Item[] = Object.values(RoleType).map((role) => ({
  label: role,
  value: role,
}));

function StaffInfoTab({ userId, user }: Props) {

  const [staffInfo, setStaffInfo] = useState<StaffInfo | null>(null);

  const [isReadOnly, setIsReadOnly] = useState<boolean>(true);
  const [gender, setGender] = useState<string>("");
  const [nric, setNric] = useState<string>("");

  const [dateOfBirth, setDateOfBirth] = useState<Date | undefined>(undefined);
  const [race, setRace] = useState<string>("");
  const [nationality, setNationality] = useState<string>("");
  const [err, setErr] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => {
    const fetchStaffInfo = async () => {
      if (!userId) return;
      const staffInfo = await getStaffInfoByUserId(userId);
      console.log("staffInfo", staffInfo);
      setStaffInfo(staffInfo);
      setGender(staffInfo.gender);
      setNric(staffInfo.nric);
      setDateOfBirth(new Date(staffInfo.dateOfBirth));
      setRace(staffInfo.race);
      setNationality(staffInfo.nationality);
    };

    fetchStaffInfo();
  }, [userId]);

  const handleCancel = () => {
    setErr("");
    setIsReadOnly(true);
    if (!staffInfo) return;
    setGender(staffInfo.gender);
    setNric(staffInfo.nric);
    setDateOfBirth(new Date(staffInfo.dateOfBirth));
    setRace(staffInfo.race);
    setNationality(staffInfo.nationality);
  };

  const isNoChange = () => {
    const res =
      staffInfo?.dateOfBirth === dateOfBirth &&
      staffInfo?.nric === nric &&
      staffInfo.gender === gender &&
      staffInfo.nationality === nationality &&
      staffInfo.race === race;
    return res;
  };

  const isValid = () => {
    if (
      nric.length === 0 ||
      race.length === 0 ||
      nationality.length === 0 ||
      !dateOfBirth
    ) {
      setErr("Please fill in all compulsory fields.");
      return false;
    }
    setErr("");
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    setIsSubmitting(true);
    e.preventDefault();
    if (!isValid() || !staffInfo) {
      setIsSubmitting(false);
      // set save btn as red

      return;
    }

    try {
      // API call to update staff info
      const updatedStaffInfo: CreateStaffInfo = {
        dateOfBirth: dateOfBirth!,
        nric,
        gender,
        race,
        nationality,
      };
      const res = await updateStaffInfoByStaffInfoId(
        staffInfo.id,
        updatedStaffInfo
      );
      console.log(res);
      if (res) {
        setStaffInfo(res);
        console.log("Staff info updated", res);
        toast({
          title: "Staff information updated!",
          description: "Staff information has been successfully updated",
        });
        setIsReadOnly(true);
      } else {
        toast({
          title: "An error occurred",
          description: "Failed to update staff information. Please try again.",
          variant: "destructive",
        });
      }
    } catch (err) {
      console.log(err);
      toast({
        title: "An error occurred",
        description: "Failed to update staff information. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  if (!staffInfo) return <div>Loading...</div>;
  return (
    <div className="flex flex-col gap-3 p-5">
      <div className="flex justify-between">
        <div>
          <div className="text-lg font-bold">Staff Information</div>
          <div className="w-full flex flex-col">
            <div
              className={` text-gray-500 text-sm transition-all duration-200 h-12`}
            >
              {isReadOnly
                ? "Basic information about the staff."
                : "Edit information below."}
            </div>
          </div>
        </div>
        <EditButton
          handleEditing={() => setIsReadOnly(false)}
          isEditing={isReadOnly}
          handleCancel={handleCancel}
          isNoChange={isNoChange}
          handleSave={handleSubmit}
          isSubmitting={isSubmitting}
        />
      </div>

      <form className="w-full h-full flex flex-col gap-6">
        <Dropdown
          label="Gender"
          items={genderOptions}
          setSelectedString={setGender}
          selectedString={gender}
          isReadOnly={isReadOnly}
        />
        <TextInput
          type="text"
          label="NRIC/FIN *"
          value={nric}
          setValue={setNric}
          readOnly={isReadOnly}
          capitalize={true}
        />
        <CustomDatePicker
          date={dateOfBirth}
          setDate={(v) => setDateOfBirth(v)}
          defaultPlaceholder="Date of Birth"
          disabled={isReadOnly}
          label="Date of Birth *"
        />
        <div className="flex w-full justify-between gap-2">
          <Dropdown
            label="Nationality *"
            items={nationalityOptions}
            setSelectedString={setNationality}
            selectedString={nationality}
            isReadOnly={isReadOnly}
          />
          <Dropdown
            label="Race *"
            items={raceOptions}
            setSelectedString={setRace}
            selectedString={race}
            isReadOnly={isReadOnly}
          />
        </div>

        {err && <div className="text-red-500 text-sm">{err}</div>}
      </form>
    </div>
  );
}

export default StaffInfoTab;
